import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import {
  Autorenew as AutorenewIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { AppColors } from '../theme/colors';
import { AccessLink, ensureAccessLink, rotateAccessLink } from '../services/accessLinkService';
import {
  buildAccessLinkMessage,
  saveAccessLinkPoster,
  saveQrCodeImage,
} from '../utils/accessLinkPoster';

interface AccessLinkPanelProps {
  companyName: string;
  corporateCode: string;
  /**
   * 「URLを切り替える」を出すか。切り替えると今のQRコードが使えなくなり、
   * 掲示物の貼り替えが要るため、システム管理者に限る
   */
  canRotate: boolean;
  /** 保存・コピー・切り替えの結果を、呼び出し元のお知らせ（Snackbar）に出す */
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

/**
 * 投稿用のQRコード・URL（専用URL）。QRコード・URLの画面（AccessLinkPage）で使う。
 *
 * 以前はここに会社IDとパスワードを表示していたが、投稿時の入力が投稿率を下げるという
 * 要望を受け、読み取るだけでログインできる専用URLを扱う形に変更した。
 *
 * URL に含まれるのは合言葉であり、パスワードではない。漏洩した場合や
 * 掲示物を貼り替える場合は「切り替え」で入れ替える。
 *
 * 会社ごとに1本の運用とする。拠点を分けたい場合は会社を分けて登録する
 * 運用（1会社＝1契約）が既に存在するため、この画面で拠点名を扱うと
 * 投稿が拠点ごとに分かれるという誤解を生む。データ上は名前を持てるため、
 * 用途を分けて発行する要件が出た場合は画面側の変更だけで対応できる。
 *
 * 合言葉は Firestore のルールでクライアントから読めないため、Cloud Functions 経由で取得する。
 * 「発行する」という操作は設けず、まだ無ければ表示したときに作る。
 *
 * 設計書: レフリアメモ/認証の一本化について.md（11章）
 */
export const AccessLinkPanel: React.FC<AccessLinkPanelProps> = ({
  companyName,
  corporateCode,
  canRotate,
  onNotify,
}) => {
  const [accessLinks, setAccessLinks] = useState<AccessLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<AccessLink | null>(null);
  const [rotating, setRotating] = useState(false);

  // 会社ごとに1本の運用のため、有効なURLは1件だけ表示する。
  // 切り替えで失効した古いURLは記録として残るが、画面には出さない。
  const activeAccessLink = accessLinks.find((link) => !link.revokedAt) ?? null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const links = await ensureAccessLink();
        if (!cancelled) setAccessLinks(links);
      } catch (e) {
        console.error('[専用URL] 一覧取得エラー:', e);
        if (!cancelled) setError((e instanceof Error && e.message) || 'URLの取得に失敗しました');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    onNotify('クリップボードにコピーしました', 'success');
  };

  /** 掲示・配布用の画像を保存する */
  const handlePosterSave = async (elementId: string, fileName: string) => {
    try {
      await saveAccessLinkPoster({ elementId, fileName, companyName, url: activeAccessLink?.url ?? '' });
      onNotify('掲示用の画像を保存しました', 'success');
    } catch (e) {
      console.error('掲示用画像の保存エラー:', e);
      onNotify('掲示用の画像の保存に失敗しました', 'error');
    }
  };

  const handleQrCodeSave = async (elementId: string, fileName: string) => {
    try {
      await saveQrCodeImage(elementId, fileName);
      onNotify('QRコードを保存しました', 'success');
    } catch (e) {
      console.error('QRコード保存エラー:', e);
      onNotify('QRコードの保存に失敗しました', 'error');
    }
  };

  /**
   * URLを切り替える。
   * 掲示物を貼り替える運用ではこちらが基本の操作になる。
   */
  const handleRotate = async () => {
    if (!rotateTarget) return;

    setRotating(true);
    try {
      const newLink = await rotateAccessLink(rotateTarget.token);
      setAccessLinks((prev) => [
        newLink,
        ...prev.map((link) =>
          link.token === rotateTarget.token ? { ...link, revokedAt: new Date().toISOString() } : link
        ),
      ]);
      setRotateTarget(null);
      onNotify('新しいURLに切り替えました。QRコードを貼り替えてください', 'success');
    } catch (e) {
      console.error('[専用URL] 切り替えエラー:', e);
      onNotify((e instanceof Error && e.message) || 'URLの切り替えに失敗しました', 'error');
    } finally {
      setRotating(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: 13, color: AppColors.textSecondary, mb: 2 }}>
        読み取るだけで投稿できるQRコードです。会社IDとパスワードの入力は不要になります。
        有効期限はありません。
        {canRotate && (
          <>
            掲示物を貼り替えたいときや、URLが外部に漏れたときは
            「URLを切り替える」を押してください。新しいQRコードに入れ替わり、古いQRコードは
            使えなくなります（今ログインしている方も、順次ログアウトされます）。
          </>
        )}
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !activeAccessLink ? (
        <Alert severity="error">QRコードを用意できませんでした。時間をおいて再度お試しください。</Alert>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'white',
              borderRadius: 3,
              border: `1px solid ${AppColors.border}`,
              display: 'inline-block',
              mb: 1,
            }}
          >
            <QRCodeSVG
              id={`access-qr-${activeAccessLink.token}`}
              value={activeAccessLink.url}
              size={220}
              level="M"
              includeMargin={false}
            />
          </Box>

          <Typography sx={{ fontSize: 12, color: AppColors.textSecondary, mb: 2 }}>
            このQRコードで {activeAccessLink.useCount} 回ログインされています
          </Typography>

          {/* 配布用（印刷・連絡ツールのどちらにも使える） */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() =>
                handlePosterSave(
                  `access-qr-${activeAccessLink.token}`,
                  `${corporateCode || 'company'}_投稿用QR.png`
                )
              }
            >
              掲示用の画像を保存
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => copy(buildAccessLinkMessage(companyName, activeAccessLink.url))}
              sx={{ color: AppColors.primary, borderColor: AppColors.primary }}
            >
              案内文をコピー
            </Button>
          </Box>

          <Typography sx={{ fontSize: 12, color: AppColors.textSecondary, mt: 1.5 }}>
            印刷して掲示するほか、連絡ツールで送ることもできます
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* 個別の素材（自作の資料に貼りたい場合） */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={<SaveIcon />}
              onClick={() =>
                handleQrCodeSave(
                  `access-qr-${activeAccessLink.token}`,
                  `${corporateCode || 'company'}_qrcode.png`
                )
              }
              sx={{ color: AppColors.textSecondary }}
            >
              QRコードだけ保存
            </Button>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={() => copy(activeAccessLink.url)}
              sx={{ color: AppColors.textSecondary }}
            >
              URLだけコピー
            </Button>
            {canRotate && (
              <Button
                size="small"
                startIcon={<AutorenewIcon />}
                onClick={() => setRotateTarget(activeAccessLink)}
                sx={{ color: AppColors.primary }}
              >
                URLを切り替える
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* 切り替えの確認 */}
      <Dialog open={Boolean(rotateTarget)} onClose={() => !rotating && setRotateTarget(null)}>
        <DialogTitle>URLを切り替えますか？</DialogTitle>
        <DialogContent>
          <DialogContentText>
            新しいQRコード・URLを発行し、今のQRコードは使えなくなります。
            <br />
            <br />
            <strong>掲示しているQRコードの貼り替えが必要です。</strong>
            切り替えたあと、新しいQRコードを保存してください。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateTarget(null)} disabled={rotating}>
            キャンセル
          </Button>
          <Button onClick={handleRotate} variant="contained" disabled={rotating}>
            {rotating ? <CircularProgress size={20} color="inherit" /> : '切り替える'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
